<template>
  <StepItem
    format="form"
    title="Verify Phone Number"
    :subtitle="subtitle"
    @handle-next-step="handleNextStep"
    @handle-prev-step="handlePrevStep"
    can-go-to-prev-step
    :loading="loading"
    :is-ready-for-next-step="
      store.phoneVerificationCode.length === 6
    "
  >
    <template v-slot:default>
      <FormVerificationCodeInput
        v-model="store.phoneVerificationCode"
      />
      <p class="text-sm text-slate-500 select-none text-center mt-8">
        Didn't receive a code?
        <b
          @click="requestNewVerificationCode"
          class="hover:underline cursor-pointer"
        >
          <span v-if="countdown !== 0" class="w-92"
          >Request again in {{ countdown }}s</span
          >
          <span v-else>Send new verification code</span>
        </b>
      </p>
    </template>
    <template v-slot:next-step-text>
      <span v-if="store.isGiveaway">🎫 </span>
      Verify
      <span v-if="store.isGiveaway">& Confirm Entry</span>
      <span v-else>Account</span>
    </template>
  </StepItem>
</template>

<script setup>
import StepItem from "./StepItem.vue";
import {computed, onActivated, onMounted, ref} from "vue";
import {useCandidateAccountCreateStore} from "../stores/CandidateAccountCreateStore.js";
import FormVerificationCodeInput from "./FormVerificationCodeInput.vue";
import {useAccountStore} from "../stores/AccountStore.js";

const accountStore = useAccountStore();
const store = useCandidateAccountCreateStore();
const isReadyForNextStep = ref(false);
const loading = ref(false);
const countdown = ref(0);

const emits = defineEmits(["complete", "prev-step"]);

const handleNextStep = async () => {
  isReadyForNextStep.value = store.validateStep3();
  if (!isReadyForNextStep.value || loading.value) return;
  loading.value = true;

  try {
    await store.register((wasSuccessful, error) => {
      if (wasSuccessful && !error) {
        emits("complete");
      } else if (error?.data && error.data.errors) {
        if ("email" in error.data.errors) emits("prev-step", 2);
        if ("first" in error.data.errors) emits("prev-step", 2);
        if ("last" in error.data.errors) emits("prev-step", 2);
        if ("phone" in error.data.errors) emits("prev-step", 1);
      } else {
        toast.error("Something went wrong. Please try again later.");
      }

      loading.value = false;
    });
  } finally {
    loading.value = false;
  }
};

const handlePrevStep = () => emits("prev-step", 1);

const requestNewVerificationCode = async () => {
  if (countdown.value !== 0 || loading.value) return;
  loading.value = true;

  try {
    await store.sendVerificationSms((success) => {
      if (success !== false) {
        toast.success("Verification code sent!");
        restartTimer();
        loading.value = false;
      }
    });
  } catch (e) {
    emits("prev-step", 1);
  } finally {
    loading.value = false;
  }
};

const restartTimer = () => {
  if (countdown.value === 0) countdown.value = 59;
  setTimeout(() => {
    countdown.value -= 1;
    if (countdown.value > 0) {
      restartTimer();
    }
  }, 1000);
};

const formatPhone = (value) => {
  if (!value) return "";
  let cleaned = value.toString();
  const match = cleaned.match(/^(\d{1,3})(\d{0,3})(\d{0,4})$/);
  if (match) {
    cleaned = `${match[1] ? "(" : ""}${match[1]}${match[2] ? ") " : ""}${
      match[2]
    }${match[3] ? "-" : ""}${match[3]}`;
  } else {
    const match = cleaned.slice(-10).match(/^(\d{1,3})(\d{0,3})(\d{0,4})$/);
    if (match) {
      cleaned =
        "+" +
        cleaned.slice(0, cleaned.length - 10) +
        " " +
        `${match[1] ? "(" : ""}${match[1]}${match[2] ? ") " : ""}${match[2]}${
          match[3] ? "-" : ""
        }${match[3]}`;
    }
  }
  return cleaned;
};

const subtitle = computed(() => {
  if (!store.isGiveaway) return "Code sent to " + formatPhone(store.phone);
  return "You're entry is counted once you verify you own " + formatPhone(store.phone);
});

onActivated(() => {
  if (accountStore.isLoggedIn) {
    toast.info("You're already logged in, no need to repeat this step...");
    emits("complete");
    return;
  }

  if (!store.phone) emits("prev-step", 1);
  requestNewVerificationCode();
});

onMounted(() => {
  if (accountStore.isLoggedIn) {
    toast.info("You're already logged in, no need to repeat this step...");
    emits("complete");
    return;
  }

  if (!store.phone) emits("prev-step", 1);
  requestNewVerificationCode();
});
</script>
